import { hasSelectedEntity } from '../../../shared/utils/entitySelection';
export function getDocumentStatusMeta(status) {
  switch (status) {
    case 'firmato':
      return { label: 'Firmato', className: 'bg-[var(--success)] text-[var(--on-primary)]' };
    case 'approvato':
      return { label: 'Approvato', className: 'bg-[var(--primary)] text-[var(--on-primary)]' };
    case 'in_revisione':
      return { label: 'In Revisione', className: 'bg-[var(--warning)] text-[var(--on-primary)]' };
    case 'completato':
      return { label: 'Completato', className: 'bg-[var(--surface-raised)] text-[var(--on-primary)]' };
    case 'pagata':
      return { label: 'Pagata', className: 'bg-[var(--success)] text-[var(--on-primary)]' };
    default:
      return { label: status, className: 'bg-[var(--surface-raised)] text-[var(--on-primary)]' };
  }
}

export function formatDocumentDate(dateString) {
  return new Date(dateString).toLocaleDateString('it-IT');
}

export function filterDocuments(documents, {
  searchTerm = '',
  selectedCategory = 'Tutti',
  selectedStatuses = [],
  selectedClientId = 'tutti',
}) {
  return documents.filter((document) => {
    const normalizedSearch = searchTerm.toLowerCase();
    const matchesSearch =
      String(document.name || '').toLowerCase().includes(normalizedSearch) ||
      String(document.clientResolvedName || document.client || '').toLowerCase().includes(normalizedSearch);
    const matchesCategory =
      selectedCategory === 'Tutti' || document.category === selectedCategory;
    const matchesStatus =
      !Array.isArray(selectedStatuses) ||
      selectedStatuses.length === 0 ||
      selectedStatuses.includes(document.status);
    const matchesClient =
      selectedClientId === 'tutti' ||
      hasSelectedEntity(document, 'client', selectedClientId);

    return matchesSearch && matchesCategory && matchesStatus && matchesClient;
  });
}

export function calculateDocumentStats(documents) {
  return [
    {
      label: 'Totale Documenti',
      value: documents.length,
      color: 'text-[var(--primary)]',
      bgColor: 'bg-[var(--primary-soft)]',
    },
    {
      label: 'Firmati',
      value: documents.filter((item) => item.status === 'firmato').length,
      color: 'text-[var(--success-text)]',
      bgColor: 'bg-[var(--success-soft)]',
    },
    {
      label: 'In Revisione',
      value: documents.filter((item) => item.status === 'in_revisione').length,
      color: 'text-[var(--warning-text)]',
      bgColor: 'bg-[var(--warning-soft)]',
    },
    {
      label: 'Questo Mese',
      value: documents.filter(
        (item) => new Date(item.uploadDate).getMonth() === new Date().getMonth()
      ).length,
      color: 'text-[var(--chart-accent-1)]',
      bgColor: 'bg-[var(--chart-accent-1-soft)]',
    },
  ];
}

const PDF_MIME_TYPES = new Set(['application/pdf']);
const IMAGE_MIME_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
  'image/svg+xml',
]);
const TEXT_MIME_TYPES = new Set([
  'text/plain',
  'text/csv',
  'application/json',
  'application/xml',
  'text/xml',
  'application/log',
]);
const OFFICE_EXTENSIONS = new Set(['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']);

export function getDocumentFileExtension(document) {
  const fileName = String(document?.fileName || document?.name || '').toLowerCase();
  const match = fileName.match(/\.([a-z0-9]+)$/);
  return match ? match[1] : '';
}

export function resolveDocumentPreviewMimeType(document) {
  const mimeType = String(document?.mimeType || '').trim().toLowerCase();
  const extension = getDocumentFileExtension(document);

  if (!mimeType || mimeType === 'application/octet-stream' || mimeType === 'binary/octet-stream' || mimeType === 'application/unknown') {
    if (extension === 'pdf') return 'application/pdf';
    if (extension === 'png') return 'image/png';
    if (extension === 'jpg' || extension === 'jpeg') return 'image/jpeg';
    if (extension === 'webp') return 'image/webp';
    if (extension === 'gif') return 'image/gif';
    if (extension === 'svg') return 'image/svg+xml';
    if (extension === 'txt') return 'text/plain';
    if (extension === 'csv') return 'text/csv';
    if (extension === 'json') return 'application/json';
    if (extension === 'xml') return 'application/xml';
    if (extension === 'log') return 'application/log';
    if (extension === 'doc') return 'application/msword';
    if (extension === 'docx') return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    if (extension === 'xls') return 'application/vnd.ms-excel';
    if (extension === 'xlsx') return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    if (extension === 'ppt') return 'application/vnd.ms-powerpoint';
    if (extension === 'pptx') return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
  }

  return mimeType;
}

export function getDocumentPreviewKind(document) {
  const mimeType = resolveDocumentPreviewMimeType(document);
  const extension = getDocumentFileExtension(document);

  if (PDF_MIME_TYPES.has(mimeType)) return 'pdf';
  if (IMAGE_MIME_TYPES.has(mimeType)) return 'image';
  if (TEXT_MIME_TYPES.has(mimeType) || extension === 'log') return 'text';
  if (OFFICE_EXTENSIONS.has(extension)) return 'unsupported';

  return 'unsupported';
}

export function canPreviewDocument(document) {
  return (
    document?.isSnapshotStale !== true &&
    Boolean(document?.id)
  );
}

export function canDownloadDocument(document) {
  return (
    document?.isSnapshotStale !== true &&
    Boolean(document?.downloadUrl) &&
    Boolean(document?.fileName)
  );
}

export function downloadDocumentFile(document) {
  if (document?.isSnapshotStale === true) {
    return {
      ok: false,
      error: 'DOCUMENT_SNAPSHOT_STALE',
      message: 'Download non disponibile da snapshot locale non aggiornato.',
    };
  }

  if (!document?.downloadUrl || !document?.fileName) {
    return {
      ok: false,
      error: 'DOCUMENT_DOWNLOAD_UNAVAILABLE',
      message: 'Download documento non disponibile.',
    };
  }

  const link = window.document.createElement('a');
  link.href = document.downloadUrl;
  link.download = document.fileName;
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';

  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);

  return {
    ok: true,
    error: null,
    message: 'Download avviato.',
  };
}
